import { Assessment, ChevronLeft, Download, Event, Groups2, Home, Language, Money, Rule, Settings } from "@mui/icons-material";
import { ListItem, ListSubheader, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlMeClient, GqlPermissions } from "~/__generated__";
import GaiaMenuItem from "~/modules/app/components/GaiaMenuItem";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import { GqlProjectMetadata } from "../../api/useMeGetProjectInfoQuery";
import useCreateProjectUrl from "../../hooks/useCreateProjectUrl";

interface Props {
    client: GqlMeClient;
    project: GqlProjectMetadata;
}

export default function ProjectMenu({ client, project }: Props) {
    const { t } = useTranslation("project");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const isClientUser = client.permissions.length > 0;
    const createProjectUrl = useCreateProjectUrl();

    return (
        <>
            {isAdmin || isClientUser ? (
                <GaiaMenuItem to={`c/${client.client.slug}`} label={client.client.name} icon={<ChevronLeft />} matchExact />
            ) : (
                <ListItem disablePadding>
                    <Typography margin={2} variant="h6">
                        {client.client.name}
                    </Typography>
                </ListItem>
            )}
            <ListSubheader sx={{ backgroundColor: "background.default" }}>
                <Typography margin={1} variant="body2">
                    {project.name}
                </Typography>
            </ListSubheader>

            <GaiaMenuItem to={createProjectUrl(".", { project })} label={t("projectMenu.overview")} icon={<Home />} matchExact />
            <GaiaMenuItem
                to={createProjectUrl("map-editor", { project })}
                label={t("projectMenu.mapView")}
                icon={<Language />}
                matchExact
            />
            <GaiaMenuItem
                to={createProjectUrl("planned", { project })}
                label={t("projectMenu.planning")}
                icon={<Event />}
                matchExact
            />
            <GaiaMenuItem to={createProjectUrl("protocols", { project })} label={t("projectMenu.protocols")} icon={<Rule />} />

            <GaiaMenuItem
                to={createProjectUrl("export", { project })}
                label={t("projectMenu.projectExport")}
                icon={<Download />}
            />

            {isAdmin && (
                <>
                    <GaiaMenuItem
                        to={createProjectUrl("deliverables", { project })}
                        label={t("projectMenu.deliverables")}
                        icon={<Assessment />}
                    />
                    <GaiaMenuItem to={createProjectUrl("users", { project })} label={t("projectMenu.users")} icon={<Groups2 />} />
                    <GaiaMenuItem to={createProjectUrl("cost")} label={t("projectMenu.cost")} icon={<Money />} />
                    <GaiaMenuItem to={createProjectUrl("settings")} label={t("projectMenu.settings")} icon={<Settings />} />
                </>
            )}
        </>
    );
}
